import { useEffect, useMemo, useState } from 'react';
import { getImageUrl, getPersonExternalIds } from '../../services/tmdb';
import type { CastMember } from '../../types';

interface CastCarouselProps {
  cast: CastMember[];
}

const imdbProfileUrl = (imdbId: string) => `https://www.imdb.com/name/${imdbId}/`;
const imdbSearchUrl = (name: string) =>
  `https://www.imdb.com/find/?q=${encodeURIComponent(name)}&s=nm`;

export default function CastCarousel({ cast }: CastCarouselProps) {
  const visibleCast = useMemo(() => cast.slice(0, 12), [cast]);
  const [imdbIds, setImdbIds] = useState<Record<number, string | null>>({});

  useEffect(() => {
    let cancelled = false;

    Promise.allSettled(
      visibleCast.map(async (person) => ({
        personId: person.id,
        imdbId: (await getPersonExternalIds(person.id)).imdb_id ?? null,
      }))
    ).then((results) => {
      if (cancelled) return;

      const resolved: Record<number, string | null> = {};
      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          resolved[result.value.personId] = result.value.imdbId;
        } else {
          resolved[visibleCast[index].id] = null;
        }
      });
      setImdbIds(resolved);
    });

    return () => {
      cancelled = true;
    };
  }, [visibleCast]);

  if (visibleCast.length === 0) return null;

  return (
    <section className="mt-12" aria-labelledby="cast-heading">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-2">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-emerald-300/75">
            People behind the story
          </p>
          <h2 id="cast-heading" className="mt-1 text-2xl font-semibold">Cast</h2>
        </div>
        <p className="text-xs text-gray-500">Select a person for their IMDb profile</p>
      </div>

      <div className="flex gap-4 overflow-x-auto hide-scrollbar pb-4">
        {visibleCast.map((person) => {
          const imdbId = imdbIds[person.id];
          const isResolving = imdbId === undefined;
          const href = isResolving
            ? undefined
            : imdbId
              ? imdbProfileUrl(imdbId)
              : imdbSearchUrl(person.name);

          return (
            <a
              key={person.id}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              aria-disabled={isResolving}
              onClick={(event) => {
                if (isResolving) event.preventDefault();
              }}
              className={`group flex w-28 flex-shrink-0 flex-col text-center outline-none ${isResolving ? 'cursor-wait opacity-70' : ''}`}
              aria-label={`Open ${person.name}'s IMDb profile`}
            >
              <span className="relative mx-auto mb-2 block h-28 w-28 overflow-hidden rounded-full border border-white/10 bg-white/5 transition group-hover:-translate-y-1 group-hover:border-yellow-400/60 group-focus-visible:ring-2 group-focus-visible:ring-yellow-400">
                <img
                  src={getImageUrl(person.profile_path, 'w185')}
                  alt=""
                  className="h-full w-full object-cover"
                  loading="lazy"
                  onError={(event) => {
                    event.currentTarget.src = '/placeholder-person.svg';
                  }}
                />
                <span className="absolute bottom-1 right-1 rounded bg-[#f5c518] px-1.5 py-0.5 text-[9px] font-black text-black shadow-lg">
                  IMDb
                </span>
              </span>
              <span className="line-clamp-1 text-sm font-medium text-white group-hover:text-yellow-300">
                {person.name}
              </span>
              <span className="line-clamp-2 text-xs leading-snug text-gray-400">
                {person.character || 'Cast member'}
              </span>
            </a>
          );
        })}
      </div>
    </section>
  );
}
